import React from "react";
import axios from "axios";
import { Phone, Mail, MapPin, Clock, Send, Loader2, CheckCircle2 } from "lucide-react";
import { SITE } from "../lib/site";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = React.useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = React.useState(false);
  const [done, setDone] = React.useState(false);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      return toast.error("Compila nome, email e messaggio");
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setDone(true);
      toast.success("Richiesta inviata. Ti ricontatteremo presto!");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      toast.error(`Invio fallito: ${detail}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contatti" className="py-20 md:py-28 bg-white border-t border-brand-line" data-testid="contact-section">
      <div className="container-page grid lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5">
          <span className="eyebrow">Contattaci</span>
          <h2 className="heading-display text-3xl md:text-5xl mt-4">
            Parliamo del tuo prossimo progetto.
          </h2>
          <p className="text-brand-mute mt-5 leading-relaxed">
            Sopralluogo e preventivo gratuiti in tutta Liguria. Risposta entro 24 ore lavorative.
          </p>

          <ul className="mt-8 space-y-4">
            <li className="flex items-start gap-4">
              <div className="h-10 w-10 rounded-xl bg-brand-terracotta/10 text-brand-terracotta flex items-center justify-center"><Phone className="h-5 w-5" /></div>
              <div>
                <div className="text-xs uppercase tracking-widest text-brand-mute">Telefono</div>
                {SITE.phones.map((p) => (
                  <a key={p} href={`tel:${p.replace(/\s/g, "")}`} className="block font-semibold text-brand-ink hover:text-brand-terracotta">{p}</a>
                ))}
              </div>
            </li>
            <li className="flex items-start gap-4">
              <div className="h-10 w-10 rounded-xl bg-brand-terracotta/10 text-brand-terracotta flex items-center justify-center"><Mail className="h-5 w-5" /></div>
              <div>
                <div className="text-xs uppercase tracking-widest text-brand-mute">Email</div>
                <a href={`mailto:${SITE.email}`} className="font-semibold text-brand-ink hover:text-brand-terracotta">{SITE.email}</a>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <div className="h-10 w-10 rounded-xl bg-brand-terracotta/10 text-brand-terracotta flex items-center justify-center"><MapPin className="h-5 w-5" /></div>
              <div>
                <div className="text-xs uppercase tracking-widest text-brand-mute">Sede</div>
                <div className="font-semibold text-brand-ink">{SITE.address}</div>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <div className="h-10 w-10 rounded-xl bg-brand-terracotta/10 text-brand-terracotta flex items-center justify-center"><Clock className="h-5 w-5" /></div>
              <div>
                <div className="text-xs uppercase tracking-widest text-brand-mute">Orari</div>
                <div className="font-semibold text-brand-ink">{SITE.hours}</div>
              </div>
            </li>
          </ul>
        </div>

        <form onSubmit={submit} className="lg:col-span-7 card-tactile p-6 md:p-10" data-testid="contact-form">
          {done && (
            <div className="mb-6 p-4 rounded-xl bg-brand-sage/10 border border-brand-sage/30 flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-brand-sage shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold text-brand-ink">Richiesta inviata!</div>
                <div className="text-sm text-brand-mute">Ti ricontatteremo entro 24 ore lavorative.</div>
              </div>
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Nome e cognome *">
              <input value={form.name} onChange={onChange("name")} className="input-edil" placeholder="Mario Rossi" data-testid="contact-name" required />
            </Field>
            <Field label="Email *">
              <input value={form.email} onChange={onChange("email")} type="email" className="input-edil" placeholder="mario@email.it" data-testid="contact-email" required />
            </Field>
            <Field label="Telefono">
              <input value={form.phone} onChange={onChange("phone")} className="input-edil" placeholder="+39 ..." data-testid="contact-phone" />
            </Field>
            <Field label="Tipo intervento" hint="(opzionale, indica nel messaggio)">
              <input className="input-edil" placeholder="es. Ristrutturazione bagno" disabled />
            </Field>
          </div>
          <Field label="Messaggio *">
            <textarea value={form.message} onChange={onChange("message")} rows={5} className="input-edil" placeholder="Descrivi il progetto: tipo di lavoro, metratura, tempistiche..." data-testid="contact-message" required />
          </Field>

          <div className="mt-6 flex flex-wrap items-center gap-4 justify-between">
            <p className="text-xs text-brand-mute max-w-md">
              Inviando la richiesta accetti di essere ricontattato. I dati non saranno condivisi con terzi.
            </p>
            <button type="submit" disabled={loading} className="btn-primary" data-testid="contact-submit">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              {loading ? "Invio..." : "Invia richiesta"}
            </button>
          </div>

          <style>{`.input-edil{width:100%;border-radius:.75rem;border:1px solid #E5E3DB;background:#fff;padding:.75rem 1rem;font-size:.95rem;outline:none;transition:all .15s}.input-edil:focus{border-color:#B85C38;box-shadow:0 0 0 3px rgba(184,92,56,.15)}`}</style>
        </form>
      </div>
    </section>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="block mt-4 first:mt-0">
      <span className="text-sm font-semibold text-brand-ink">
        {label} {hint && <span className="font-normal text-brand-mute">{hint}</span>}
      </span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
